import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { botsAPI } from "../services/api";

import {
  ArrowLeft,
  Plus,
  Trash2,
  Globe,
  FileText,
  MessageSquare,
  Save,
  ToggleLeft,
  ToggleRight,
  Loader,
} from "lucide-react";

const TABS = [
  "General",
  "Appearance",
  "Knowledge",
  "Lead Capture",
  "Meeting Booking",
  "Advanced",
];
export default function BotEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bot, setBot] = useState(null);
  const [tab, setTab] = useState("General");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  // Knowledge state
  const [qaForm, setQaForm] = useState({ question: "", answer: "" });
  const [textForm, setTextForm] = useState("");
  const [urlForm, setUrlForm] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    botsAPI
      .get(id)
      .then((r) => {
        setBot(r.data.bot);
        setForm(r.data.bot);
      })
      .catch(() => {
        toast.error("Bot not found");
        navigate("/bots");
      });
  }, [id, navigate]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await botsAPI.update(id, form);
      setBot(data.bot);
      toast.success("Changes saved");
    } catch (err) {
      toast.error(err.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const f = (key) => ({
    value: form[key] ?? "",
    onChange: (e) => setForm((v) => ({ ...v, [key]: e.target.value })),
  });

  const nested = (parent, key) => ({
    value: form[parent]?.[key] ?? "",
    onChange: (e) =>
      setForm((v) => ({
        ...v,
        [parent]: { ...v[parent], [key]: e.target.value },
      })),
  });

  const nestedBool = (parent, key) => ({
    checked: !!form[parent]?.[key],
    onChange: (e) =>
      setForm((v) => ({
        ...v,
        [parent]: { ...v[parent], [key]: e.target.checked },
      })),
  });

  // Knowledge actions
  const addQA = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const { data } = await botsAPI.addQA(id, qaForm);
      setBot((b) => ({ ...b, knowledge: data.knowledge }));
      setForm((b) => ({ ...b, knowledge: data.knowledge }));
      setQaForm({ question: "", answer: "" });
      toast.success("Q&A added");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed");
    } finally {
      setAdding(false);
    }
  };

  const addText = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const { data } = await botsAPI.addText(id, { content: textForm });
      setBot((b) => ({ ...b, knowledge: data.knowledge }));
      setForm((b) => ({ ...b, knowledge: data.knowledge }));
      setTextForm("");
      toast.success("Text added");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed");
    } finally {
      setAdding(false);
    }
  };

  const addUrl = async (e) => {
    e.preventDefault();
    setAdding(true);
    toast("Scraping URL… this may take 10-20 seconds", { icon: "🌐" });
    try {
      const { data } = await botsAPI.scrapeUrl(id, { url: urlForm });
      setBot((b) => ({ ...b, knowledge: data.knowledge }));
      setForm((b) => ({ ...b, knowledge: data.knowledge }));
      setUrlForm("");
      toast.success("URL scraped and added");
    } catch (err) {
      toast.error(err.response?.data?.error || "Scraping failed");
    } finally {
      setAdding(false);
    }
  };

  const deleteKnowledge = async (kid) => {
    try {
      const { data } = await botsAPI.deleteKnowledge(id, kid);
      setBot((b) => ({ ...b, knowledge: data.knowledge }));
      setForm((b) => ({ ...b, knowledge: data.knowledge }));
      toast.success("Entry deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const toggleKnowledge = async (kid) => {
    try {
      await botsAPI.toggleKnowledge(id, kid);
      setBot((b) => ({
        ...b,
        knowledge: b.knowledge.map((k) =>
          k._id === kid ? { ...k, isActive: !k.isActive } : k,
        ),
      }));
      setForm((b) => ({
        ...b,
        knowledge: b.knowledge.map((k) =>
          k._id === kid ? { ...k, isActive: !k.isActive } : k,
        ),
      }));
    } catch {
      toast.error("Failed");
    }
  };

  if (!bot)
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
        <div className="spinner" />
      </div>
    );

  return (
    <div>
      <div
        className="page-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Link to="/bots" className="btn btn-ghost btn-icon">
            <ArrowLeft size={18} />
          </Link>
          <div>
            <h1 style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {bot.avatarEmoji} {bot.name}
            </h1>
            <p>Edit chatbot settings and knowledge base</p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <Link to={`/bots/${id}/embed`} className="btn btn-secondary">
            Get embed code
          </Link>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? (
              <>
                <Loader
                  size={14}
                  style={{ animation: "spin .7s linear infinite" }}
                />{" "}
                Saving…
              </>
            ) : (
              <>
                <Save size={14} /> Save changes
              </>
            )}
          </button>
        </div>
      </div>

      <div className="tabs" style={{ marginBottom: 24 }}>
        {TABS.map((t) => (
          <button
            key={t}
            className={`tab${tab === t ? " active" : ""}`}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {/* ── General ── */}
      {tab === "General" && (
        <div
          className="card"
          style={{ display: "flex", flexDirection: "column", gap: 18 }}
        >
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
          >
            {[
              { k: "name", l: "Bot name", req: true },
              { k: "description", l: "Description" },
              { k: "businessName", l: "Business name" },
              { k: "businessType", l: "Business type" },
              {
                k: "businessHours",
                l: "Business hours",
                p: "e.g. Mon-Fri 9am-5pm",
              },
              { k: "businessPhone", l: "Phone number" },
              { k: "businessEmail", l: "Business email" },
              { k: "businessAddress", l: "Address" },
            ].map((fld) => (
              <div className="form-group" key={fld.k}>
                <label className="form-label">{fld.l}</label>
                <input
                  className="input"
                  type="text"
                  placeholder={fld.p || ""}
                  required={!!fld.req}
                  {...f(fld.k)}
                />
              </div>
            ))}
          </div>
          <div className="form-group">
            <label className="form-label">System prompt (advanced)</label>
            <textarea
              className="textarea"
              rows={4}
              placeholder="Custom AI instructions..."
              {...f("systemPrompt")}
            />
            <span className="form-hint">
              Leave blank to use auto-generated prompt from business info above
            </span>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 16,
            }}
          >
            <div className="form-group">
              <label className="form-label">Welcome message</label>
              <input className="input" {...f("welcomeMessage")} />
            </div>
            <div className="form-group">
              <label className="form-label">Fallback message</label>
              <input className="input" {...f("fallbackMessage")} />
            </div>
            <div className="form-group">
              <label className="form-label">Language</label>
              <select className="select" {...f("language")}>
                <option value="en">English</option>
                <option value="ar">Arabic</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* ── Appearance ── */}
      {tab === "Appearance" && (
        <div
          className="card"
          style={{ display: "flex", flexDirection: "column", gap: 18 }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
              gap: 16,
            }}
          >
            <div className="form-group">
              <label className="form-label">Primary color</label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="color"
                  value={form.theme?.primaryColor || "#1a9e5c"}
                  onChange={(e) =>
                    setForm((v) => ({
                      ...v,
                      theme: { ...v.theme, primaryColor: e.target.value },
                    }))
                  }
                />
                <span style={{ fontSize: 13, color: "var(--text2)" }}>
                  {form.theme?.primaryColor || "#1a9e5c"}
                </span>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Position</label>
              <select
                className="select"
                value={form.theme?.position || "bottom-right"}
                onChange={(e) =>
                  setForm((v) => ({
                    ...v,
                    theme: { ...v.theme, position: e.target.value },
                  }))
                }
              >
                <option value="bottom-right">Bottom right</option>
                <option value="bottom-left">Bottom left</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Launcher text</label>
              <input
                className="input"
                value={form.theme?.launcherText || ""}
                onChange={(e) =>
                  setForm((v) => ({
                    ...v,
                    theme: { ...v.theme, launcherText: e.target.value },
                  }))
                }
                placeholder="Chat with us"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Avatar emoji</label>
              <input
                className="input"
                {...f("avatarEmoji")}
                placeholder="🤖"
                maxLength={4}
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: 20 }}>
            {[
              { k: "showBotAvatar", l: "Show bot avatar" },
              { k: "showBranding", l: 'Show "Powered by Wispy"' },
            ].map(({ k, l }) => (
              <label
                key={k}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  cursor: "pointer",
                  fontSize: 14,
                }}
              >
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={!!form.theme?.[k]}
                    onChange={(e) =>
                      setForm((v) => ({
                        ...v,
                        theme: { ...v.theme, [k]: e.target.checked },
                      }))
                    }
                  />
                  <span className="toggle-slider" />
                </label>
                {l}
              </label>
            ))}
          </div>

          {/* Preview */}
          <div style={{ marginTop: 8 }}>
            <div className="form-label" style={{ marginBottom: 10 }}>
              Preview
            </div>
            <div
              style={{
                background: "var(--bg3)",
                borderRadius: "var(--radius-lg)",
                padding: 20,
                display: "flex",
                alignItems: "flex-end",
                justifyContent:
                  form.theme?.position === "bottom-left"
                    ? "flex-start"
                    : "flex-end",
                minHeight: 120,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems:
                    form.theme?.position === "bottom-left"
                      ? "flex-start"
                      : "flex-end",
                  gap: 8,
                }}
              >
                {form.theme?.launcherText && (
                  <div
                    style={{
                      background: "var(--surface)",
                      borderRadius: "var(--radius)",
                      padding: "7px 14px",
                      fontSize: 13,
                      color: "var(--text)",
                      border: "1px solid var(--border)",
                    }}
                  >
                    {form.theme.launcherText}
                  </div>
                )}
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: "50%",
                    background: form.theme?.primaryColor || "#1a9e5c",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 22,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                  }}
                >
                  {form.avatarEmoji || "🤖"}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Knowledge ── */}
      {tab === "Knowledge" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Add Q&A */}
          <div className="card">
            <h3
              style={{
                fontFamily: "var(--font-head)",
                fontSize: 15,
                fontWeight: 700,
                marginBottom: 14,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <MessageSquare size={16} style={{ color: "var(--brand)" }} /> Add
              Q&A Pair
            </h3>
            <form
              onSubmit={addQA}
              style={{ display: "flex", flexDirection: "column", gap: 12 }}
            >
              <div className="form-group">
                <label className="form-label">Question</label>
                <input
                  className="input"
                  placeholder="What are your business hours?"
                  value={qaForm.question}
                  onChange={(e) =>
                    setQaForm((v) => ({ ...v, question: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Answer</label>
                <textarea
                  className="textarea"
                  rows={3}
                  placeholder="We're open Monday to Friday, 9am to 5pm EST."
                  value={qaForm.answer}
                  onChange={(e) =>
                    setQaForm((v) => ({ ...v, answer: e.target.value }))
                  }
                  required
                />
              </div>
              <button
                className="btn btn-primary btn-sm"
                type="submit"
                disabled={adding}
                style={{ alignSelf: "flex-start" }}
              >
                {adding ? (
                  "Adding…"
                ) : (
                  <>
                    <Plus size={14} /> Add Q&A
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Add URL */}
          <div className="card">
            <h3
              style={{
                fontFamily: "var(--font-head)",
                fontSize: 15,
                fontWeight: 700,
                marginBottom: 14,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Globe size={16} style={{ color: "var(--blue)" }} /> Scrape URL
            </h3>
            <form onSubmit={addUrl} style={{ display: "flex", gap: 10 }}>
              <input
                className="input"
                type="url"
                placeholder="https://yourwebsite.com/about"
                value={urlForm}
                onChange={(e) => setUrlForm(e.target.value)}
                required
                style={{ flex: 1 }}
              />
              <button
                className="btn btn-secondary"
                type="submit"
                disabled={adding}
              >
                {adding ? (
                  <>
                    <Loader
                      size={14}
                      style={{ animation: "spin .7s linear infinite" }}
                    />{" "}
                    Scraping…
                  </>
                ) : (
                  "Scrape"
                )}
              </button>
            </form>
            <p className="form-hint" style={{ marginTop: 8 }}>
              AI will extract and summarize the page content automatically
            </p>
          </div>

          {/* Add Text */}
          <div className="card">
            <h3
              style={{
                fontFamily: "var(--font-head)",
                fontSize: 15,
                fontWeight: 700,
                marginBottom: 14,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <FileText size={16} style={{ color: "var(--amber)" }} /> Add
              Custom Text
            </h3>
            <form
              onSubmit={addText}
              style={{ display: "flex", flexDirection: "column", gap: 10 }}
            >
              <textarea
                className="textarea"
                rows={5}
                placeholder="Paste any relevant text, FAQ, product info, policies..."
                value={textForm}
                onChange={(e) => setTextForm(e.target.value)}
                required
              />
              <button
                className="btn btn-secondary btn-sm"
                type="submit"
                disabled={adding}
                style={{ alignSelf: "flex-start" }}
              >
                {adding ? (
                  "Adding…"
                ) : (
                  <>
                    <Plus size={14} /> Add text
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Knowledge list */}
          {bot.knowledge?.length > 0 && (
            <div className="card">
              <h3
                style={{
                  fontFamily: "var(--font-head)",
                  fontSize: 15,
                  fontWeight: 700,
                  marginBottom: 14,
                }}
              >
                Knowledge Base ({bot.knowledge.filter((k) => k.isActive).length}{" "}
                active)
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {bot.knowledge.map((k) => (
                  <div
                    key={k._id}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      padding: "10px 12px",
                      background: "var(--bg3)",
                      borderRadius: "var(--radius)",
                      border: "1px solid var(--border)",
                      opacity: k.isActive ? 1 : 0.5,
                    }}
                  >
                    <span
                      className={`badge badge-${k.type === "qa" ? "green" : k.type === "url" ? "blue" : "amber"}`}
                      style={{ flexShrink: 0, marginTop: 1 }}
                    >
                      {k.type}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {k.type === "qa" && (
                        <>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 500,
                              marginBottom: 3,
                            }}
                          >
                            {k.question}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: "var(--text3)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {k.answer}
                          </div>
                        </>
                      )}
                      {k.type === "url" && (
                        <div style={{ fontSize: 13 }}>{k.url}</div>
                      )}
                      {k.type === "text" && (
                        <div
                          style={{
                            fontSize: 12,
                            color: "var(--text2)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {k.content}
                        </div>
                      )}
                    </div>
                    <button
                      className="btn btn-ghost btn-icon btn-sm"
                      onClick={() => toggleKnowledge(k._id)}
                      title={k.isActive ? "Disable" : "Enable"}
                    >
                      {k.isActive ? (
                        <ToggleRight
                          size={16}
                          style={{ color: "var(--brand)" }}
                        />
                      ) : (
                        <ToggleLeft
                          size={16}
                          style={{ color: "var(--text3)" }}
                        />
                      )}
                    </button>
                    <button
                      className="btn btn-ghost btn-icon btn-sm"
                      style={{ color: "var(--red)" }}
                      onClick={() => deleteKnowledge(k._id)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Lead Capture ── */}
      {tab === "Lead Capture" && (
        <div
          className="card"
          style={{ display: "flex", flexDirection: "column", gap: 18 }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                Enable lead capture
              </div>
              <div style={{ fontSize: 13, color: "var(--text3)" }}>
                Automatically collect visitor contact info
              </div>
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                {...nestedBool("leadCapture", "enabled")}
              />
              <span className="toggle-slider" />
            </label>
          </div>
          <div className="divider" style={{ margin: "4px 0" }} />
          {[
            { k: "askName", l: "Ask for name" },
            { k: "askEmail", l: "Ask for email" },
            { k: "askPhone", l: "Ask for phone" },
          ].map(({ k, l }) => (
            <div
              key={k}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span style={{ fontSize: 14 }}>{l}</span>
              <label className="toggle">
                <input type="checkbox" {...nestedBool("leadCapture", k)} />
                <span className="toggle-slider" />
              </label>
            </div>
          ))}
          <div className="form-group">
            <label className="form-label">Trigger message</label>
            <input
              className="input"
              value={form.leadCapture?.message || ""}
              placeholder="Could I get your name and email to follow up?"
              onChange={(e) =>
                setForm((v) => ({
                  ...v,
                  leadCapture: { ...v.leadCapture, message: e.target.value },
                }))
              }
            />
          </div>
          <div className="form-group">
            <label className="form-label">Ask after N messages</label>
            <input
              className="input"
              type="number"
              min={1}
              max={10}
              value={form.leadCapture?.triggerAfter || 2}
              onChange={(e) =>
                setForm((v) => ({
                  ...v,
                  leadCapture: {
                    ...v.leadCapture,
                    triggerAfter: +e.target.value,
                  },
                }))
              }
            />
          </div>
        </div>
      )}

      {/* ── Meeting Booking ── */}
      {tab === "Meeting Booking" && (
        <div
          className="card"
          style={{ display: "flex", flexDirection: "column", gap: 18 }}
        >
          {/* Enable toggle */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                Enable meeting booking
              </div>
              <div style={{ fontSize: 13, color: "var(--text3)" }}>
                Bot will detect booking intent and schedule meetings
                automatically
              </div>
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                {...nestedBool("meetingBooking", "enabled")}
              />
              <span className="toggle-slider" />
            </label>
          </div>

          <div className="divider" style={{ margin: "4px 0" }} />

          {/* Duration */}
          <div className="form-group">
            <label className="form-label">Meeting duration (minutes)</label>
            <select
              className="input"
              value={form.meetingBooking?.duration || 30}
              onChange={(e) =>
                setForm((v) => ({
                  ...v,
                  meetingBooking: {
                    ...v.meetingBooking,
                    duration: +e.target.value,
                  },
                }))
              }
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>1 hour</option>
              <option value={90}>1.5 hours</option>
            </select>
          </div>

          {/* Available days */}
          <div className="form-group">
            <label className="form-label">Available days</label>
            <input
              className="input"
              value={form.meetingBooking?.availableDays || ""}
              placeholder="Monday - Friday"
              onChange={(e) =>
                setForm((v) => ({
                  ...v,
                  meetingBooking: {
                    ...v.meetingBooking,
                    availableDays: e.target.value,
                  },
                }))
              }
            />
          </div>

          {/* Available hours */}
          <div className="form-group">
            <label className="form-label">Available hours</label>
            <input
              className="input"
              value={form.meetingBooking?.availableHours || ""}
              placeholder="9am - 5pm"
              onChange={(e) =>
                setForm((v) => ({
                  ...v,
                  meetingBooking: {
                    ...v.meetingBooking,
                    availableHours: e.target.value,
                  },
                }))
              }
            />
          </div>

          {/* Timezone */}
          <div className="form-group">
            <label className="form-label">Timezone</label>
            <input
              className="input"
              value={form.meetingBooking?.timezone || ""}
              placeholder="UTC, America/New_York, Europe/London..."
              onChange={(e) =>
                setForm((v) => ({
                  ...v,
                  meetingBooking: {
                    ...v.meetingBooking,
                    timezone: e.target.value,
                  },
                }))
              }
            />
          </div>

          {/* Confirmation message */}
          <div className="form-group">
            <label className="form-label">Confirmation message</label>
            <textarea
              className="input"
              rows={3}
              value={form.meetingBooking?.confirmationMsg || ""}
              placeholder="Your meeting has been booked! You'll receive a confirmation shortly."
              style={{ resize: "vertical" }}
              onChange={(e) =>
                setForm((v) => ({
                  ...v,
                  meetingBooking: {
                    ...v.meetingBooking,
                    confirmationMsg: e.target.value,
                  },
                }))
              }
            />
          </div>
        </div>
      )}

      {/* ── Advanced ── */}
      {tab === "Advanced" && (
        <div
          className="card"
          style={{ display: "flex", flexDirection: "column", gap: 18 }}
        >
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
          >
            <div className="form-group">
              <label className="form-label">AI Model</label>
              <select className="select" {...f("aiModel")}>
                <option value="gpt-4o">GPT-4o (best)</option>
                <option value="gpt-4-turbo">GPT-4 Turbo</option>
                <option value="gpt-3.5-turbo">GPT-3.5 Turbo (fastest)</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Temperature (creativity)</label>
              <input
                className="input"
                type="number"
                min={0}
                max={1}
                step={0.1}
                {...f("temperature")}
              />
              <span className="form-hint">0 = precise, 1 = creative</span>
            </div>
            <div className="form-group">
              <label className="form-label">Max tokens per reply</label>
              <input
                className="input"
                type="number"
                min={50}
                max={2000}
                {...f("maxTokens")}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Allowed domains (one per line)</label>
            <textarea
              className="textarea"
              rows={4}
              placeholder="example.com&#10;www.example.com"
              value={(form.allowedDomains || []).join("\n")}
              onChange={(e) =>
                setForm((v) => ({
                  ...v,
                  allowedDomains: e.target.value
                    .split("\n")
                    .map((s) => s.trim())
                    .filter(Boolean),
                }))
              }
            />
            <span className="form-hint">Leave empty to allow all domains</span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div style={{ fontWeight: 500, fontSize: 14 }}>Bot active</div>
              <div style={{ fontSize: 12, color: "var(--text3)" }}>
                Disable to pause the bot without deleting
              </div>
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                checked={!!form.isActive}
                onChange={(e) =>
                  setForm((v) => ({ ...v, isActive: e.target.checked }))
                }
              />
              <span className="toggle-slider" />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
